/**
 * MobyCalendar™ — Type definitions for the P2P calendar system
 * 
 * Architecture:
 * - Full event details stored in Dexie (local), synced P2P via chat channel
 * - MongoDB presence layer stores ONLY availability bitmaps (no meeting info)
 * - PendingDelivery system used for offline invite notifications
 * 
 * Privacy model:
 * - Meeting titles, descriptions, attendees, locations are NEVER sent to server
 * - Only time-slot availability (busy/free bitmap) touches MongoDB
 * - All event data travels exclusively over encrypted P2P data channels
 */

// ─── Enums ───────────────────────────────────────────────────────────────────

/** RSVP status for a calendar event attendee */
export enum CalendarRSVP {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  TENTATIVE = 'tentative',
}

/** Recurrence frequency (subset of RFC 5545 RRULE) */
export enum RecurrenceFrequency {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  BIWEEKLY = 'BIWEEKLY',
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
}

/** Calendar event visibility for availability bitmap */
export enum EventVisibility {
  BUSY = 'busy',           // Shows as busy in availability
  FREE = 'free',           // Does not block availability
  TENTATIVE = 'tentative', // Shows as tentative in availability
}

/** Reminder delivery method */
export enum ReminderMethod {
  NOTIFICATION = 'notification', // Browser notification
  SOUND = 'sound',               // Audio alert
}

/** Calendar sync action types sent over P2P */
export enum CalendarSyncAction {
  EVENT_CREATE = 'event_create',
  EVENT_UPDATE = 'event_update',
  EVENT_DELETE = 'event_delete',
  INVITE = 'invite',
  RSVP = 'rsvp',
  AVAILABILITY_REQUEST = 'availability_request',
  AVAILABILITY_RESPONSE = 'availability_response',
}

// ─── Core Interfaces ─────────────────────────────────────────────────────────

/** A single attendee in a calendar event */
export interface CalendarAttendee {
  address: string;           // Peer public key
  displayName?: string;      // Cached display name for offline rendering
  rsvp: CalendarRSVP;
  respondedAt?: number;      // Unix ms when RSVP was last updated
  isOrganizer?: boolean;     // True for the event creator
}

/** Recurrence rule (simplified RFC 5545) */
export interface RecurrenceRule {
  frequency: RecurrenceFrequency;
  interval?: number;         // Every N frequency units (default 1)
  count?: number;            // Stop after N occurrences
  until?: number;            // Stop after this date (Unix ms)
  byDay?: number[];          // Day of week (0=Sun, 6=Sat) for WEEKLY
  byMonthDay?: number[];     // Day of month for MONTHLY
  exceptions?: number[];     // Excluded dates (Unix ms, start of day)
}

/** Reminder configuration */
export interface CalendarReminder {
  minutesBefore: number;     // Minutes before event start
  method: ReminderMethod;
}

/** Full calendar event — stored in Dexie, synced P2P only */
export interface CalendarEvent {
  id: string;                  // UUID
  title: string;
  description?: string;
  location?: string;           // Free text or URL (e.g., "https://meet.moby.chat/room123")
  
  // Timing
  startTime: number;           // Unix ms
  endTime: number;             // Unix ms
  allDay: boolean;
  timezone: string;            // IANA timezone (e.g., "America/Los_Angeles")
  
  // Recurrence
  recurrence?: RecurrenceRule;
  recurringEventId?: string;   // Parent event ID if this is a recurrence exception
  
  // Visibility & reminders
  visibility: EventVisibility;
  reminders: CalendarReminder[];
  
  // Ownership
  organizerAddress: string;    // Creator's public key
  attendees: CalendarAttendee[];
  
  // Group support
  groupAddress?: string;       // If this is a group meeting
  
  // Sync metadata
  vectorClock: Record<string, number>;
  createdAt: number;           // Unix ms
  updatedAt: number;           // Unix ms
  deletedAt?: number;          // Soft delete for sync (Unix ms)
  
  // Color tag for UI
  color?: string;              // Hex color override
}

/** Calendar invite — tracks invite flow state */
export interface CalendarInvite {
  id: string;                  // UUID
  eventId: string;             // References CalendarEvent.id
  
  // Parties
  senderAddress: string;       // Who sent the invite
  recipientAddress: string;    // Who receives it
  
  // State
  rsvp: CalendarRSVP;
  sentAt: number;              // Unix ms
  respondedAt?: number;        // Unix ms
  
  // Delivery tracking
  deliveredP2P: boolean;       // True if delivered via data channel
  pendingDeliveryId?: string;  // References PendingDeliveryEntity.id if queued for offline
  
  // Minimal event snapshot for offline display
  eventSnapshot: {
    title: string;
    startTime: number;
    endTime: number;
    allDay: boolean;
    organizerName?: string;
    groupName?: string;
  };
}

// ─── Availability Bitmap ─────────────────────────────────────────────────────

/**
 * Availability bitmap for MongoDB presence layer.
 * 
 * Encodes 7 days × 96 quarter-hour slots = 672 bits = 84 bytes.
 * Each bit: 0 = free, 1 = busy.
 * Day 0 = today, slot 0 = 00:00-00:15 in the user's timezone.
 * 
 * This is the ONLY calendar data that touches MongoDB.
 */
export interface AvailabilityBitmap {
  timezone: string;            // IANA timezone
  slots: string;               // Base64-encoded 84-byte bitmap
  updatedAt: number;           // Unix ms
  windowStart: number;         // Unix ms of day 0 midnight in user's timezone
}

/** Decoded availability for UI display */
export interface AvailabilitySlot {
  startTime: number;           // Unix ms
  endTime: number;             // Unix ms
  isBusy: boolean;
}

// ─── P2P Sync Protocol ──────────────────────────────────────────────────────

/** Calendar sync message payload sent over chat data channel */
export interface CalendarSyncPayload {
  action: CalendarSyncAction;
  timestamp: number;           // Unix ms
  senderAddress: string;
  
  // Polymorphic payload based on action
  event?: CalendarEvent;       // For EVENT_CREATE, EVENT_UPDATE
  eventId?: string;            // For EVENT_DELETE
  invite?: CalendarInvite;     // For INVITE
  rsvp?: {                     // For RSVP
    eventId: string;
    inviteId: string;
    status: CalendarRSVP;
  };
  availability?: AvailabilityBitmap; // For AVAILABILITY_RESPONSE
  
  // For group events — target group address
  groupAddress?: string;
}

/** Metadata stored in PendingDeliveryEntity for offline calendar invites */
export interface CalendarPendingDeliveryMetadata {
  eventId: string;
  action: 'invite' | 'update' | 'cancel';
  organizerAddress: string;
  organizerName?: string;
  eventTitle: string;          // Minimal info for notification display
  startTime: number;
  endTime: number;
  allDay?: boolean;
  groupAddress?: string;
  groupName?: string;
}

// ─── Utility Types ───────────────────────────────────────────────────────────

/** Calendar view mode for the calendar panel */
export type CalendarViewMode = 'month' | 'week' | 'day' | 'agenda';

/** Calendar settings stored in localStorage */
export interface CalendarSettings {
  defaultView: CalendarViewMode;
  timezone: string;
  weekStartsOn: 0 | 1;        // 0 = Sunday, 1 = Monday
  workingHoursStart: number;   // Hour (0-23), default 9
  workingHoursEnd: number;     // Hour (0-23), default 17
  defaultReminders: CalendarReminder[];
  defaultVisibility: EventVisibility;
  publishAvailability: boolean; // Whether to publish bitmap to MongoDB
}

/** Default calendar settings */
export const DEFAULT_CALENDAR_SETTINGS: CalendarSettings = {
  defaultView: 'month',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  weekStartsOn: 0,
  workingHoursStart: 9,
  workingHoursEnd: 17,
  defaultReminders: [
    { minutesBefore: 15, method: ReminderMethod.NOTIFICATION },
  ],
  defaultVisibility: EventVisibility.BUSY,
  publishAvailability: true,
};

// ─── Availability Bitmap Helpers ─────────────────────────────────────────────

const SLOTS_PER_DAY = 96;  // 24h × 4 quarter-hours
const BITMAP_DAYS = 7;
const BITMAP_BITS = SLOTS_PER_DAY * BITMAP_DAYS; // 672
const BITMAP_BYTES = Math.ceil(BITMAP_BITS / 8);  // 84

/** Create an empty availability bitmap (all free) */
export function createEmptyBitmap(timezone: string): AvailabilityBitmap {
  const bytes = new Uint8Array(BITMAP_BYTES);
  return {
    timezone,
    slots: uint8ArrayToBase64(bytes),
    updatedAt: Date.now(),
    windowStart: getMidnightTimestamp(timezone),
  };
}

/** Set a slot as busy in the bitmap */
export function setBitmapSlot(bitmap: AvailabilityBitmap, dayIndex: number, slotIndex: number, busy: boolean): AvailabilityBitmap {
  const bytes = base64ToUint8Array(bitmap.slots);
  const bitIndex = dayIndex * SLOTS_PER_DAY + slotIndex;
  const byteIndex = Math.floor(bitIndex / 8);
  const bitOffset = bitIndex % 8;
  
  if (busy) {
    bytes[byteIndex] |= (1 << bitOffset);
  } else {
    bytes[byteIndex] &= ~(1 << bitOffset);
  }
  
  return {
    ...bitmap,
    slots: uint8ArrayToBase64(bytes),
    updatedAt: Date.now(),
  };
}

/** Check if a specific slot is busy */
export function isBitmapSlotBusy(bitmap: AvailabilityBitmap, dayIndex: number, slotIndex: number): boolean {
  const bytes = base64ToUint8Array(bitmap.slots);
  const bitIndex = dayIndex * SLOTS_PER_DAY + slotIndex;
  const byteIndex = Math.floor(bitIndex / 8);
  const bitOffset = bitIndex % 8;
  return (bytes[byteIndex] & (1 << bitOffset)) !== 0;
}

/** Build availability bitmap from calendar events */
export function buildAvailabilityFromEvents(
  events: CalendarEvent[],
  timezone: string,
): AvailabilityBitmap {
  const bitmap = createEmptyBitmap(timezone);
  const windowStart = bitmap.windowStart;
  const windowEnd = windowStart + BITMAP_DAYS * 24 * 60 * 60 * 1000;
  const bytes = base64ToUint8Array(bitmap.slots);
  
  for (const event of events) {
    if (event.deletedAt) continue;
    if (event.visibility === EventVisibility.FREE) continue;
    if (event.endTime <= windowStart || event.startTime >= windowEnd) continue;
    
    // Calculate which slots this event covers
    const eventStart = Math.max(event.startTime, windowStart);
    const eventEnd = Math.min(event.endTime, windowEnd);
    
    const startSlot = Math.floor((eventStart - windowStart) / (15 * 60 * 1000));
    const endSlot = Math.ceil((eventEnd - windowStart) / (15 * 60 * 1000));
    
    for (let slot = startSlot; slot < endSlot && slot < BITMAP_BITS; slot++) {
      const byteIndex = Math.floor(slot / 8);
      const bitOffset = slot % 8;
      bytes[byteIndex] |= (1 << bitOffset);
    }
  }
  
  return {
    ...bitmap,
    slots: uint8ArrayToBase64(bytes),
    updatedAt: Date.now(),
  };
}

/** Decode bitmap into human-readable availability slots for a specific day */
export function decodeDayAvailability(bitmap: AvailabilityBitmap, dayIndex: number): AvailabilitySlot[] {
  const slots: AvailabilitySlot[] = [];
  const dayStart = bitmap.windowStart + dayIndex * 24 * 60 * 60 * 1000;
  const quarterHourMs = 15 * 60 * 1000;
  
  let currentBusy: boolean | null = null;
  let blockStart = 0;
  
  for (let i = 0; i < SLOTS_PER_DAY; i++) {
    const busy = isBitmapSlotBusy(bitmap, dayIndex, i);
    
    if (busy !== currentBusy) {
      if (currentBusy !== null) {
        slots.push({
          startTime: dayStart + blockStart * quarterHourMs,
          endTime: dayStart + i * quarterHourMs,
          isBusy: currentBusy,
        });
      }
      currentBusy = busy;
      blockStart = i;
    }
  }
  
  // Close final block
  if (currentBusy !== null) {
    slots.push({
      startTime: dayStart + blockStart * quarterHourMs,
      endTime: dayStart + SLOTS_PER_DAY * quarterHourMs,
      isBusy: currentBusy,
    });
  }
  
  return slots;
}

// ─── Internal Helpers ────────────────────────────────────────────────────────

function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Get midnight timestamp for today in the given timezone */
function getMidnightTimestamp(timezone: string): number {
  const now = new Date();
  // Format in the target timezone to get local date components
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  
  const year = parseInt(parts.find(p => p.type === 'year')!.value);
  const month = parseInt(parts.find(p => p.type === 'month')!.value) - 1;
  const day = parseInt(parts.find(p => p.type === 'day')!.value);
  
  // Create a date at midnight in UTC, then adjust for timezone offset
  // This is an approximation — for precise timezone handling, 
  // a library like Temporal or date-fns-tz would be ideal
  const midnight = new Date(year, month, day, 0, 0, 0, 0);
  return midnight.getTime();
}
